/**
 * TREASURED — Saved Search (Phase 1B front-end + Phase 2A hub integration)
 *
 * Isolated add-on. Nothing here touches Shopify search, Search & Discovery, Prefixbox
 * or the Phase 1A predictive-search fix; it only reads the current /search URL and the
 * filter labels already rendered on the page.
 *
 * Architecture
 * ------------
 *  SavedSearchStore            – abstract storage contract (list / save / remove / has)
 *  LocalStorageSavedSearchStore – Phase 1B implementation (namespaced localStorage)
 *  HubSavedSearchStore         – Phase 2A implementation for /apps/hub/saved-searches
 *                                (GET / POST / DELETE) — used for logged-in customers when
 *                                the theme setting `saved_search_hub_enabled` is on.
 *  buildSearchState()          – turns the current search URL into a canonical record
 *  <treasured-save-search>     – "Save this search" control on the results page
 *  <treasured-saved-searches>  – "Saved searches" list inside the search modal
 *
 * Storage is chosen once in getStore() from the `data-store` attribute the Liquid snippets
 * render; the UI never talks to localStorage or fetch() directly.
 */

const STORAGE_KEY = 'treasured_saved_searches_v1';
const MAX_ITEMS = 50;
const CHANGE_EVENT = 'treasured:saved-searches:change';

/** URL parameters that never define a search (pagination, layout, tracking, preview). */
const IGNORED_PARAMS = new Set([
  'page',
  'grid',
  'section_id',
  'sections',
  '_pos',
  '_psq',
  '_psid',
  '_ss',
  'preview_theme_id',
  '_fd',
  '_sc',
  '_ab',
  'fbclid',
  'gclid',
  'key',
]);

const isIgnoredParam = (name) => IGNORED_PARAMS.has(name) || name.startsWith('utm_');

/** Deterministic ordering shared with the Treasured Hub (server) — see normalise.ts there. */
const byCodeUnit = (a, b) => (a < b ? -1 : a > b ? 1 : 0);

/**
 * Horizon's facets rewrite price bounds as "500.00" / "3,000.00" while a hand-typed URL has
 * "500" / "3000". Normalise both to plain numbers so the same search always gets the same id.
 * @param {string} value
 */
const normalisePrice = (value) => {
  const number = Number.parseFloat(String(value).replace(/[^\d.]/g, ''));
  return Number.isFinite(number) ? String(number) : value;
};

/**
 * Parameters that define the customer-visible search CRITERIA and therefore the saved-search
 * IDENTITY (used for de-duplication). Everything else that survives the ignore-list is kept in
 * the navigation URL (so the search reopens exactly as it was) but does not create a separate
 * record. `options[prefix]` is the prime example: the modal form adds `options[prefix]=last`,
 * the results-page input does not, and the shopper sees the same search either way.
 * @param {string} name
 */
const isIdentityParam = (name) =>
  name === 'q' ||
  name === 'type' ||
  name === 'sort_by' ||
  name.startsWith('filter.') ||
  name === 'options[unavailable_products]' ||
  name === 'options[fields]';

/** Identity-level normalisation of a value (case/whitespace-insensitive terms, default sort). */
const identityValue = (name, value) => {
  if (name === 'q') return value.trim().replace(/\s+/g, ' ').toLowerCase();
  return value;
};

/* ------------------------------------------------------------------ */
/* Storage contract                                                    */
/* ------------------------------------------------------------------ */

/**
 * @typedef {object} SavedSearch
 * @property {string} id            Stable id derived from `identity` (duplicates collapse)
 * @property {string} identity      Path + normalised CRITERIA only (q, type, sort, filters, price) — de-duplication key
 * @property {string} canonical     Navigation URL: path + sorted, cleaned, result-affecting query string
 * @property {string} q             Search terms
 * @property {string} type          Shopify search type (product)
 * @property {string|null} sort_by
 * @property {{param: string, label: string, values: string[]}[]} filters  Every filter.* parameter except price
 * @property {{min: string|null, max: string|null}} price
 * @property {string} locale        Locale root of the search URL ('' or '/ar')
 * @property {string} label         Display title (search terms)
 * @property {string} summary       Concise, human filter summary built at save time
 * @property {string} created_at    ISO timestamp
 */

class SavedSearchStore {
  /** @returns {Promise<SavedSearch[]>} newest first */
  async list() {
    return [];
  }
  /** @param {SavedSearch} record */
  async save(record) {
    void record;
  }
  /** @param {string} id */
  async remove(id) {
    void id;
  }
  /** @param {string} id */
  async has(id) {
    return (await this.list()).some((item) => item.id === id);
  }
  /** Phase 2B — per-search notification opt-in. Only server-backed stores can support it. */
  get supportsAlerts() {
    return false;
  }
  /**
   * @param {string} id
   * @param {boolean} enabled
   * @returns {Promise<SavedSearch | null>} the updated record, or null when unsupported
   */
  async setAlerts(id, enabled) {
    void id;
    void enabled;
    return null;
  }
}

/** Phase 1B — namespaced localStorage. Stores search criteria only, never personal data. */
class LocalStorageSavedSearchStore extends SavedSearchStore {
  #read() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((item) => item && item.id && item.canonical) : [];
    } catch {
      return [];
    }
  }

  #write(items) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_ITEMS)));
    } catch {
      /* storage unavailable (private mode / quota) — prototype degrades silently */
    }
  }

  async list() {
    return this.#read();
  }

  async save(record) {
    const items = this.#read().filter((item) => item.id !== record.id);
    items.unshift(record);
    this.#write(items);
  }

  async remove(id) {
    this.#write(this.#read().filter((item) => item.id !== id));
  }
}

/**
 * Phase 2A — Treasured Hub (Shopify App Proxy → Hub → Postgres). Same contract.
 *
 * The request goes to the storefront's own domain (/apps/hub/…); Shopify forwards it to the
 * hub and appends a signed `logged_in_customer_id`. The theme never knows or sends the
 * customer id — it only knows *whether* someone is logged in (Liquid `customer`), which
 * selects this store. Nothing is written to localStorage for logged-in customers, so a
 * shared device never leaks one customer's searches to the next.
 *
 * Failure mode: if the hub is unreachable, list() resolves to [] and save()/remove()
 * reject; the UI catches those and leaves the visible state unchanged.
 */
class HubSavedSearchStore extends SavedSearchStore {
  /** @type {SavedSearch[] | null} */
  #cache = null;
  #cacheAt = 0;
  static CACHE_MS = 60_000;

  constructor(endpoint = '/apps/hub/saved-searches') {
    super();
    this.endpoint = endpoint;
  }

  async #request(method, path = '', body) {
    const response = await fetch(`${this.endpoint}${path}`, {
      method,
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        ...(body ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (response.status === 204) return null;
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      const error = new Error(`Saved searches: ${method} ${path || '/'} failed (${response.status})`);
      error.status = response.status;
      error.code = data?.error?.code || 'request_failed';
      throw error;
    }
    return data;
  }

  invalidate() {
    this.#cache = null;
  }

  async list() {
    if (this.#cache && Date.now() - this.#cacheAt < HubSavedSearchStore.CACHE_MS) return this.#cache;
    try {
      const data = await this.#request('GET');
      this.#cache = Array.isArray(data?.items) ? data.items : [];
      this.#cacheAt = Date.now();
      return this.#cache;
    } catch (error) {
      console.warn('[treasured] saved searches unavailable', error?.code || error);
      return this.#cache || [];
    }
  }

  async save(record) {
    // Only the navigation URL and display strings travel; the hub re-derives the criteria
    // and the identity from `canonical`, so the server is the source of truth.
    const data = await this.#request('POST', '', {
      canonical: record.canonical,
      label: record.label,
      summary: record.summary,
      filters: record.filters.map(({ param, label }) => ({ param, label })),
      locale: record.locale,
    });
    const saved = data?.item || record;
    const items = (this.#cache || []).filter((item) => item.id !== saved.id);
    items.unshift(saved);
    this.#cache = items;
    this.#cacheAt = Date.now();
    return saved;
  }

  async remove(id) {
    try {
      await this.#request('DELETE', `/${encodeURIComponent(id)}`);
    } catch (error) {
      if (error?.status !== 404) throw error; // already gone → treat as removed
    }
    if (this.#cache) this.#cache = this.#cache.filter((item) => item.id !== id);
  }

  get supportsAlerts() {
    return true;
  }

  /**
   * Phase 2B — opt a saved search in/out of "new item" notifications.
   * PATCH /apps/hub/saved-searches/:id { alerts_enabled } → { item }. Rejects on any failure
   * (the UI restores its previous visual state); never touches localStorage.
   */
  async setAlerts(id, enabled) {
    const data = await this.#request('PATCH', `/${encodeURIComponent(id)}`, { alerts_enabled: enabled === true });
    const updated = data?.item;
    if (!updated || updated.id !== id) throw Object.assign(new Error('Saved searches: unexpected PATCH response'), { code: 'invalid_response' });
    if (this.#cache) this.#cache = this.#cache.map((item) => (item.id === id ? { ...item, ...updated } : item));
    return updated;
  }
}

/**
 * Store selection (Phase 2A). The Liquid snippets render `data-store="hub"` only when the
 * theme setting `saved_search_hub_enabled` is on AND a customer is logged in; otherwise
 * `data-store="local"` (guests, or hub switched off) keeps the Phase 1B behaviour.
 * Guest → account migration is intentionally NOT done here (see README, later phase).
 */
let storeInstance = null;
function getStore() {
  if (storeInstance) return storeInstance;
  const mount = document.querySelector('treasured-save-search[data-store], treasured-saved-searches[data-store]');
  const useHub = mount?.dataset.store === 'hub';
  storeInstance = useHub ? new HubSavedSearchStore(mount?.dataset.hubEndpoint || '/apps/hub/saved-searches') : new LocalStorageSavedSearchStore();
  return storeInstance;
}

/** Lazy proxy so existing call sites (`store.list()` …) keep working unchanged. */
const store = {
  list: () => getStore().list(),
  save: (record) => getStore().save(record),
  remove: (id) => getStore().remove(id),
  has: (id) => getStore().has(id),
  setAlerts: (id, enabled) => getStore().setAlerts(id, enabled),
  get supportsAlerts() {
    return getStore().supportsAlerts;
  },
  get kind() {
    return getStore() instanceof HubSavedSearchStore ? 'hub' : 'local';
  },
};

const notifyChange = () => document.dispatchEvent(new CustomEvent(CHANGE_EVENT));

/* ------------------------------------------------------------------ */
/* Search state → canonical record                                     */
/* ------------------------------------------------------------------ */

const hash = (value) => {
  let h = 5381;
  for (let i = 0; i < value.length; i++) h = ((h << 5) + h + value.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
};

const STRINGS = {
  en: {
    all: 'All items',
    from: 'From',
    upTo: 'Up to',
    sort: { 'price-ascending': 'Price: low to high', 'price-descending': 'Price: high to low', 'created-descending': 'Newest' },
  },
  ar: {
    all: 'كل المنتجات',
    from: 'من',
    upTo: 'حتى',
    sort: { 'price-ascending': 'السعر: من الأقل', 'price-descending': 'السعر: من الأعلى', 'created-descending': 'الأحدث' },
  },
};

const strings = () => (document.documentElement.lang || 'en').startsWith('ar') ? STRINGS.ar : STRINGS.en;

const currencyCode = () => window.Shopify?.currency?.active || 'AED';

const formatAmount = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  return number.toLocaleString(document.documentElement.lang || 'en', { maximumFractionDigits: 0 });
};

/**
 * Reads "filter.* parameter name → group label" from the filters rendered on the page,
 * e.g. filter.p.m.shipturtle.sp_6a046e39e61e2 → "Condition". Used only at save time.
 * @returns {Record<string, string>}
 */
function readFacetLabels() {
  const labels = {};
  for (const input of document.querySelectorAll('input[name^="filter."]')) {
    const name = input.getAttribute('name');
    if (!name || labels[name]) continue;
    const group = input.closest('details, fieldset, .facets__disclosure, [data-filter-group]');
    // Horizon: <summary class="facets__summary"><span class="facets__label">Condition</span> …status…</summary>
    const heading =
      group?.querySelector('.facets__label, [data-filter-label], legend') ||
      group?.querySelector('summary, .facets__summary');
    const text = heading?.textContent?.replace(/\s+/g, ' ').replace(/\(\d+\)/g, '').trim();
    if (text) labels[name] = text.replace(/[⌄▾↓]+$/, '').trim();
  }
  return labels;
}

const humanizeParam = (param) =>
  param
    .replace(/^filter\.(p|v)\.(m\.)?/, '')
    .split('.')
    .pop()
    .replace(/[_-]+/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());

/**
 * Builds the canonical record for a search URL.
 * @param {URL} [url]
 * @param {Record<string, string>} [facetLabels]
 * @returns {SavedSearch}
 */
export function buildSearchState(url = new URL(window.location.href), facetLabels = readFacetLabels()) {
  const params = new URLSearchParams(url.search);
  /** @type {[string, string][]} */
  const kept = [];

  for (const [name, value] of params.entries()) {
    if (isIgnoredParam(name)) continue;
    if (value.trim() === '') continue;
    if (name === 'q') kept.push([name, value.trim().replace(/\s+/g, ' ')]);
    else if (name.startsWith('filter.v.price')) kept.push([name, normalisePrice(value)]);
    else kept.push([name, value]);
  }

  if (!kept.some(([name]) => name === 'type')) kept.push(['type', 'product']);

  // Plain code-unit order (not localeCompare) so the browser and the hub sort identically.
  kept.sort(([a, av], [b, bv]) => (a === b ? byCodeUnit(av, bv) : byCodeUnit(a, b)));

  const canonicalParams = new URLSearchParams();
  for (const [name, value] of kept) canonicalParams.append(name, value);

  // Navigation URL: everything result-affecting, so the search reopens exactly as it was.
  const canonical = `${url.pathname}?${canonicalParams.toString()}`;

  // Identity: only the criteria the shopper sees (q, type, sort, filters, price) — one record
  // per customer-visible search regardless of entry point (modal vs results page).
  const identityParams = new URLSearchParams();
  for (const [name, value] of kept) {
    if (!isIdentityParam(name)) continue;
    if (name === 'sort_by' && value === 'relevance') continue; // default sort ≡ no sort
    identityParams.append(name, identityValue(name, value));
  }
  const identity = `${url.pathname}?${identityParams.toString()}`;
  const q = (params.get('q') || '').trim().replace(/\s+/g, ' ');
  const sort_by = params.get('sort_by') || null;
  const price = {
    min: canonicalParams.get('filter.v.price.gte') || null,
    max: canonicalParams.get('filter.v.price.lte') || null,
  };

  /** @type {Map<string, string[]>} */
  const grouped = new Map();
  for (const [name, value] of kept) {
    if (!name.startsWith('filter.') || name.startsWith('filter.v.price')) continue;
    if (!grouped.has(name)) grouped.set(name, []);
    grouped.get(name)?.push(value);
  }

  const filters = [...grouped.entries()].map(([param, values]) => ({
    param,
    label: facetLabels[param] || humanizeParam(param),
    values,
  }));

  const localeMatch = url.pathname.match(/^(\/[a-z]{2}(?:-[a-z]{2})?)\/search\/?$/i);
  const locale = localeMatch ? localeMatch[1] : '';

  const record = {
    id: `ss_${hash(identity)}`,
    identity,
    canonical,
    q,
    type: params.get('type') || 'product',
    sort_by,
    filters,
    price,
    locale,
    label: q || strings().all,
    summary: '',
    created_at: new Date().toISOString(),
  };
  record.summary = buildSummary(record);
  return record;
}

/**
 * "Women · Very good · Black · AED 500–3,000 · Price: low to high"
 * @param {SavedSearch} record
 */
export function buildSummary(record) {
  const t = strings();
  const parts = [];

  // Category first, then the remaining filters in page order.
  const ordered = [...record.filters].sort((a, b) => {
    const score = (f) => (/category/i.test(f.param) || /category/i.test(f.label) ? 0 : 1);
    return score(a) - score(b);
  });
  for (const filter of ordered) parts.push(filter.values.join(', '));

  const { min, max } = record.price || {};
  if (min && max) parts.push(`${currencyCode()} ${formatAmount(min)}–${formatAmount(max)}`);
  else if (min) parts.push(`${t.from} ${currencyCode()} ${formatAmount(min)}`);
  else if (max) parts.push(`${t.upTo} ${currencyCode()} ${formatAmount(max)}`);

  if (record.sort_by && record.sort_by !== 'relevance') parts.push(t.sort[record.sort_by] || record.sort_by);

  return parts.filter(Boolean).join(' · ');
}

/* ------------------------------------------------------------------ */
/* Icons                                                               */
/* ------------------------------------------------------------------ */

const bookmarkIcon = (filled) => `
  <svg class="tss-icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
    <path d="M5 3.5h10a.5.5 0 0 1 .5.5v12.3a.3.3 0 0 1-.48.24L10 13.1l-5.02 3.44a.3.3 0 0 1-.48-.24V4a.5.5 0 0 1 .5-.5Z"
      fill="${filled ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>
  </svg>`;

/** Phase 2B — notification bell: outline = alerts off, filled = alerts on. */
const bellIcon = (active) => `
  <svg class="tss-icon tss-icon--bell" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
    <path d="M10 2.75a4.75 4.75 0 0 0-4.75 4.75v3.1c0 .62-.21 1.23-.6 1.72L3.25 14.1h13.5l-1.4-1.78a2.8 2.8 0 0 1-.6-1.72V7.5A4.75 4.75 0 0 0 10 2.75Z"
      fill="${active ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M8.1 15.4a1.9 1.9 0 0 0 3.8 0" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
  </svg>`;

const escapeHtml = (value) =>
  String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char] || char);

/* ------------------------------------------------------------------ */
/* <treasured-save-search> — results page control                      */
/* ------------------------------------------------------------------ */

class TreasuredSaveSearch extends HTMLElement {
  #controller = new AbortController();
  /** @type {SavedSearch | null} */
  #state = null;
  #saved = false;

  connectedCallback() {
    const { signal } = this.#controller;
    this.#render();
    this.#sync();
    this.#alignWithSearchInput();

    // Facets update the URL with history.pushState and dispatch filter:update (Horizon facets.js).
    document.addEventListener('filter:update', () => this.#sync(), { signal });
    window.addEventListener('popstate', () => this.#sync(), { signal });
    document.addEventListener(CHANGE_EVENT, () => this.#sync(), { signal });
    window.addEventListener('resize', () => this.#alignWithSearchInput(), { signal });
  }

  /** Match the width of the theme's search input block so the control sits under its end edge. */
  #alignWithSearchInput() {
    const input = document.querySelector('search-page-input-component');
    const maxWidth = input ? getComputedStyle(input).maxWidth : '';
    this.style.maxWidth = maxWidth && maxWidth !== 'none' ? maxWidth : '';
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get #labels() {
    return {
      save: this.dataset.labelSave || 'Save this search',
      saved: this.dataset.labelSaved || 'Saved',
      unsaveHint: this.dataset.labelUnsave || 'Remove from saved searches',
    };
  }

  #render() {
    this.innerHTML = `
      <button type="button" class="tss-save" aria-pressed="false">
        ${bookmarkIcon(false)}
        <span class="tss-save__label">${escapeHtml(this.#labels.save)}</span>
      </button>`;
    this.querySelector('button')?.addEventListener('click', () => this.#toggle(), { signal: this.#controller.signal });
  }

  async #sync() {
    // Debounce: facets can fire several updates in a row.
    clearTimeout(this.#syncTimer);
    this.#syncTimer = setTimeout(async () => {
      this.#state = buildSearchState();
      this.#saved = await store.has(this.#state.id);
      this.#paint();
    }, 50);
  }
  #syncTimer = 0;
  #busy = false;

  #paint() {
    const button = this.querySelector('button');
    if (!button) return;
    button.setAttribute('aria-pressed', String(this.#saved));
    button.classList.toggle('is-saved', this.#saved);
    button.title = this.#saved ? this.#labels.unsaveHint : '';
    button.innerHTML = `${bookmarkIcon(this.#saved)}<span class="tss-save__label">${escapeHtml(
      this.#saved ? this.#labels.saved : this.#labels.save
    )}</span>`;
  }

  async #toggle() {
    if (!this.#state) this.#state = buildSearchState();
    if (this.#busy) return;
    this.#busy = true;
    try {
      if (this.#saved) {
        await store.remove(this.#state.id);
        this.#saved = false;
      } else {
        // Rebuild right before saving so the record reflects the URL at click time.
        this.#state = buildSearchState();
        const saved = await store.save(this.#state);
        if (saved && saved.id) this.#state = saved; // hub returns the authoritative record
        this.#saved = true;
      }
    } catch (error) {
      // Hub unreachable / rejected: keep the visible state truthful and move on.
      console.warn('[treasured] could not update saved search', error?.code || error);
      return;
    } finally {
      this.#busy = false;
    }
    this.#paint();
    this.classList.add('is-bumping');
    setTimeout(() => this.classList.remove('is-bumping'), 260);
    notifyChange();
  }
}

/* ------------------------------------------------------------------ */
/* <treasured-saved-searches> — list inside the search modal           */
/* ------------------------------------------------------------------ */

class TreasuredSavedSearches extends HTMLElement {
  #controller = new AbortController();
  /** @type {HTMLInputElement | null} */
  #input = null;
  /** @type {HTMLFormElement | null} */
  #form = null;
  /** @type {MutationObserver | null} */
  #modeObserver = null;

  connectedCallback() {
    const { signal } = this.#controller;
    const component = this.closest('predictive-search-component');
    this.#form = component?.querySelector('form.predictive-search-form') || this.closest('form');
    this.#input = component?.querySelector('input[name="q"]') || null;

    this.#input?.addEventListener('input', () => this.#update(), { signal });
    // Phase 1A resets the input on Escape / dialog close through the component; listen broadly.
    this.#input?.addEventListener('change', () => this.#update(), { signal });
    document.addEventListener(CHANGE_EVENT, () => this.#refresh(), { signal });
    this.closest('dialog-component')?.addEventListener('dialog:open', () => this.#refresh(), { signal });
    this.closest('dialog-component')?.addEventListener('dialog:close', () => this.#update(), { signal });

    // Members mode toggles a class on the form (search-modal.liquid); watch it.
    if (this.#form) {
      this.#modeObserver = new MutationObserver(() => this.#update());
      this.#modeObserver.observe(this.#form, { attributes: true, attributeFilter: ['class'] });
    }

    // The reset button clears the input without an input event.
    component?.querySelector('[ref="resetButton"]')?.addEventListener('click', () => setTimeout(() => this.#update(), 150), { signal });

    this.addEventListener('click', this.#handleClick, { signal });

    // Keyboard: Horizon's predictive component listens for keydown at the *document* capture
    // phase and, whenever any result cards exist (the empty state always has some), takes over
    // Tab / Shift+Tab / Enter for the whole form. A listener on `window` (capture) runs before it,
    // so keys aimed at the saved-search rows — or at the input while this list is showing — can
    // be left to their native behaviour without touching predictive-search.js.
    window.addEventListener('keydown', this.#handleKeydownCapture, { signal, capture: true });

    this.#refresh();
  }

  /** @returns {boolean} true while the predictive component has an arrow-selected result card */
  get #componentHasSelection() {
    return !!this.closest('predictive-search-component')?.querySelector(
      '.predictive-search-form__content [aria-selected="true"]'
    );
  }

  /** @param {KeyboardEvent} event */
  #handleKeydownCapture = (event) => {
    if (this.hidden || event.defaultPrevented) return;
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (!this.contains(target)) {
      // Tab from the (empty) search input or the header controls that sit between it and this
      // list (mode selector, close button): let focus move natively so the saved rows are
      // reachable, unless the shopper has already arrowed into the product cards.
      if (
        event.key === 'Tab' &&
        !this.#componentHasSelection &&
        this.#form?.contains(target) &&
        !target.closest('.predictive-search-form__content')
      ) {
        event.stopPropagation();
      }
      return;
    }

    switch (event.key) {
      case 'Tab':
      case 'Enter':
      case ' ':
        // Native: Tab/Shift+Tab move focus, Enter follows the row link, Enter/Space press the bell / bookmark.
        event.stopPropagation();
        return;
      case 'ArrowDown':
      case 'ArrowUp': {
        event.stopPropagation();
        event.preventDefault();
        const focusables = [...this.querySelectorAll('a, button')].filter((el) => el instanceof HTMLElement);
        const index = focusables.indexOf(/** @type {HTMLElement} */ (target));
        const next = focusables[index + (event.key === 'ArrowDown' ? 1 : -1)];
        (next || (event.key === 'ArrowUp' ? this.#input : focusables[focusables.length - 1]))?.focus();
        return;
      }
      default:
        return; // Escape and everything else: unchanged theme behaviour
    }
  };

  disconnectedCallback() {
    this.#controller.abort();
    this.#modeObserver?.disconnect();
  }

  get #labels() {
    return {
      title: this.dataset.labelTitle || 'Saved searches',
      remove: this.dataset.labelRemove || 'Remove saved search',
      alertsOn: this.dataset.labelAlertsOn || 'Notify me about new items',
      alertsOff: this.dataset.labelAlertsOff || 'Turn off notifications',
    };
  }

  get #isMembersMode() {
    return !!this.#form?.classList.contains('search-mode-members');
  }

  get #isEmptyInput() {
    return !this.#input || this.#input.value.trim() === '';
  }

  #items = /** @type {SavedSearch[]} */ ([]);

  async #refresh() {
    // Re-rendering replaces the rows; keep keyboard focus on the equivalent element.
    const active = document.activeElement;
    const focused =
      active instanceof HTMLElement && this.contains(active)
        ? { id: active.closest('.tss-row')?.getAttribute('data-id'), remove: active.matches('.tss-row__remove'), alerts: active.matches('.tss-row__alerts') }
        : null;

    this.#items = await store.list();
    this.#renderList();
    this.#update();

    if (focused && !this.hidden) {
      const row = focused.id ? this.querySelector(`.tss-row[data-id="${CSS.escape(focused.id)}"]`) : null;
      const el =
        row?.querySelector(focused.remove ? '.tss-row__remove' : focused.alerts ? '.tss-row__alerts' : '.tss-row__link') ||
        this.querySelector('.tss-row__remove, .tss-row__link');
      if (el instanceof HTMLElement) el.focus();
    }
  }

  #update() {
    const visible = this.#items.length > 0 && this.#isEmptyInput && !this.#isMembersMode;
    this.hidden = !visible;
  }

  #renderList() {
    if (!this.#items.length) {
      this.innerHTML = '';
      return;
    }
    // Phase 2B: the notification toggle exists only for server-backed (logged-in) searches —
    // guests' browser-only searches cannot be matched by the hub.
    const withAlerts = store.supportsAlerts;
    const rows = this.#items
      .map(
        (item) => `
        <li class="tss-row" data-id="${escapeHtml(item.id)}">
          <a class="tss-row__link" href="${escapeHtml(item.canonical)}">
            <span class="tss-row__title">${escapeHtml(item.label)}</span>
            ${item.summary ? `<span class="tss-row__meta">${escapeHtml(item.summary)}</span>` : ''}
          </a>
          ${withAlerts ? this.#renderAlertsButton(item) : ''}
          <button type="button" class="tss-row__remove" aria-label="${escapeHtml(this.#labels.remove)}: ${escapeHtml(item.label)}">
            ${bookmarkIcon(true)}
          </button>
        </li>`
      )
      .join('');

    this.innerHTML = `
      <div class="tss-list">
        <h4 class="predictive-search-results__title tss-list__title">${escapeHtml(this.#labels.title)}</h4>
        <ul class="tss-list__items list-unstyled" role="list">${rows}</ul>
      </div>`;
  }

  /** @param {SavedSearch} item */
  #renderAlertsButton(item) {
    const active = item.alerts_enabled === true;
    const label = active ? this.#labels.alertsOff : this.#labels.alertsOn;
    return `
          <button type="button" class="tss-row__alerts" aria-pressed="${active ? 'true' : 'false'}"
            aria-label="${escapeHtml(label)}: ${escapeHtml(item.label)}" title="${escapeHtml(label)}">
            ${bellIcon(active)}
          </button>`;
  }

  /**
   * Paints a bell button for the given state (used for the optimistic update and the rollback).
   * @param {HTMLElement} button
   * @param {boolean} active
   */
  #paintAlertsButton(button, active) {
    const rowLabel = button.closest('.tss-row')?.querySelector('.tss-row__title')?.textContent?.trim() || '';
    const label = active ? this.#labels.alertsOff : this.#labels.alertsOn;
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    button.setAttribute('aria-label', rowLabel ? `${label}: ${rowLabel}` : label);
    button.title = label;
    button.innerHTML = bellIcon(active);
  }

  /** @param {HTMLElement} button */
  async #toggleAlerts(button) {
    const row = button.closest('.tss-row');
    const id = row?.getAttribute('data-id');
    if (!row || !id || button.getAttribute('aria-busy') === 'true') return;

    const previous = button.getAttribute('aria-pressed') === 'true';
    const next = !previous;
    button.setAttribute('aria-busy', 'true');
    this.#paintAlertsButton(button, next); // optimistic
    try {
      const updated = await store.setAlerts(id, next);
      const confirmed = updated ? updated.alerts_enabled === true : next;
      this.#paintAlertsButton(button, confirmed);
      this.#items = this.#items.map((item) => (item.id === id ? { ...item, alerts_enabled: confirmed } : item));
    } catch (error) {
      // Fail safe: restore the previous visual state; nothing else changes.
      this.#paintAlertsButton(button, previous);
      console.warn('[treasured] could not update notification setting', error?.code || error);
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  #handleClick = async (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const alertsButton = target.closest('.tss-row__alerts');
    if (alertsButton instanceof HTMLElement) {
      // Never let the bell click reach the row link or the modal's focus handling.
      event.preventDefault();
      event.stopPropagation();
      this.#toggleAlerts(alertsButton);
      return;
    }
    const removeButton = target.closest('.tss-row__remove');
    if (!removeButton) return;

    // Never let the bookmark click reach the row link or the modal's focus handling.
    event.preventDefault();
    event.stopPropagation();

    const row = removeButton.closest('.tss-row');
    const id = row?.getAttribute('data-id');
    if (!row || !id) return;

    // Keep keyboard focus inside the modal: next row's bookmark, else the previous one, else the input.
    const buttons = [...this.querySelectorAll('.tss-row__remove')];
    const position = buttons.indexOf(removeButton);
    const focusNext = buttons[position + 1] || buttons[position - 1] || this.#input;

    row.classList.add('is-removing');
    try {
      await store.remove(id);
    } catch (error) {
      row.classList.remove('is-removing');
      console.warn('[treasured] could not remove saved search', error?.code || error);
      return;
    }
    if (document.activeElement === removeButton) focusNext?.focus();
    setTimeout(() => {
      row.remove();
      this.#items = this.#items.filter((item) => item.id !== id);
      if (!this.#items.length) this.innerHTML = '';
      this.#update();
      notifyChange();
    }, 180);
  };
}

if (!customElements.get('treasured-save-search')) customElements.define('treasured-save-search', TreasuredSaveSearch);
if (!customElements.get('treasured-saved-searches')) customElements.define('treasured-saved-searches', TreasuredSavedSearches);

export { store as savedSearchStore, SavedSearchStore, LocalStorageSavedSearchStore, HubSavedSearchStore };
